<template>
  <v-main>
    <header-components></header-components>
    <title-components></title-components>
    <v-container
      class="mx-auto mt-6"
      style="max-width: 1200px;"
    >
      <progress-components page="plan"></progress-components>
      <v-form ref="form" class="mt-12">
        <interview-link></interview-link>
        <v-card
          class="mx-auto mt-1"
          elevation="1"
          outlined
        >
          <v-card-title>
            <strong>プラン </strong><span class="text-red"><strong> *</strong></span>
          </v-card-title>
          <v-radio-group
            v-model="plan"
            class="mx-2 mb-4"
            :rules="[required]"
          >
            <v-radio class="mt-2" v-for="item in selectPlan" :key="item" :label="item" :value="item" color="doorMainColor"></v-radio>
            <v-radio value="その他" color="doorMainColor">
              <template v-slot:label>
                <v-responsive class="ml-1" max-width="344">
                  <v-text-field
                    label="その他"
                    v-model="planOther"
                    variant="underlined"
                    :rules="[requiredOtherText([plan,planOther])]"
                  ></v-text-field>
                </v-responsive>
              </template>
            </v-radio>
          </v-radio-group>
        </v-card>
        <v-card
          class="mx-auto mt-6"
          elevation="1"
          outlined
        >
          <v-card-title>
            <strong>処方箋 </strong><span class="text-red"><strong> *</strong></span>
          </v-card-title>
          <v-radio-group
            v-model="prescription"
            class="mx-2 mb-4"
            :rules="[required]"
          >
            <v-radio class="mt-2" v-for="item in selectFlagIndicators" :key="item" :label="item" :value="item" color="doorMainColor"></v-radio>
          </v-radio-group>
        </v-card>
        <v-card
          class="mx-auto mt-6"
          elevation="1"
          outlined
        >
          <div class="mx-5 my-2" v-show="isPrescriptions">
            <span><a :href="prescriptionUrl" target="_blank">処方箋URL</a>
            ※診察ID (例: 00-1)と患者様の氏名をコピペして「登録」ボタンを押下して処方箋登録をお願いします。
            ※下の「送信」ボタンも忘れなく押下してください。
            </span>
          </div>
        </v-card>
      </v-form>
    </v-container>
    <v-container>
      <v-row v-if="fromConfirm" justify="center" align-content="center" class="mb-6">
        <v-btn
          dark
          color="doorAccentColor"
          x-large min-width="362px" class="mb-4 mx-4"
          @click="clickedNextPage"
        >確認
        </v-btn>
      </v-row>
      <v-row v-else justify="center" align-content="center" class="mb-6">
        <v-btn
          dark
          color="#B0B6BA"
          x-large min-width="362px" class="mb-4 mx-4"
          @click="toBack"
        >戻る
        </v-btn>
        <v-btn
          :disabled="isDisableSendBtn"
          dark
          x-large min-width="362px"
          color="success"
          class="mb-4 mx-4 white--text"
          @click="clickedNextPage"
        >送信
        </v-btn>
      </v-row>
    </v-container>
    <error-dialog :isOpen="isError"></error-dialog>
    <footer-components></footer-components>
  </v-main>
</template>
<script>
import { defineComponent, computed, ref } from 'vue'
import { Logger } from 'aws-amplify'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import HeaderComponents from '@/components/InterviewHeader.vue'
import FooterComponents from '@/components/InterviewFooter.vue'
import TitleComponents from '@/components/MedicalRecordTitle.vue'
import ProgressComponents from '@/components/MedicalRecordProgress.vue'
import InterviewLink from '@/components/InterviewLink.vue'
import ErrorDialog from '@/components/ErrorDialog.vue'
import { useScreenOperation } from '@/func/useScreenOperation'
import { useFormValidator } from '@/func/useFormValidator'
import { usePlanValues } from '@/const/form/usePlanValues'
import { useCrossSectional } from '@/const/form/useCrossSectional'
import { useRequestConverter } from '@/func/useRequestConverter'

const logger = new Logger('MedicalRecord Plan');

export default defineComponent({
  components: {
    'header-components': HeaderComponents,
    'footer-components': FooterComponents,
    'title-components': TitleComponents,
    'progress-components': ProgressComponents,
    'interview-link': InterviewLink,
    'error-dialog': ErrorDialog
  },
  setup() {
    const { scrollToTop, fromConfirmScreen, scrollToErrorPosition } = useScreenOperation()
    const fromConfirm = computed(() => fromConfirmScreen())
    const { selectPlan } = usePlanValues()
    const { selectFlagIndicators } = useCrossSectional()
    const { required, requiredOtherText } = useFormValidator()
    const form = ref(null)
    const prescriptionUrl = ref(process.env.VUE_APP_TOKYO_PRESCRIPTION)
    const {
      toUserInfoByState,
      toRecordTimeByState,
      toRecordConsultationByState,
      toRecordAssessmentByState,
      toRecordPlanByState
    } = useRequestConverter()

    logger.info('MedicalRecord Plan')
    scrollToTop()

    const store = useStore()
    const plan = computed({
      get: () => store.state.medicalRecord.plan,
      set: (value) => store.dispatch('medicalRecord/setPlan', value)
    })
    const planOther = computed({
      get: () => store.state.medicalRecord.planOther,
      set: (value) => store.dispatch('medicalRecord/setPlanOther', value)
    })
    const prescription = computed({
      get: () => store.state.medicalRecord.prescription,
      set: (value) => store.dispatch('medicalRecord/setPrescription', value)
    })

    const isPrescriptions = computed(() => prescription.value === '有')
    const isDisableSendBtn = ref(false)
    const errorDialog = ref(false)
    const isError = computed(() => errorDialog)

    const router = useRouter()
    const toBack = () => router.go(-1)

    const updateInterview = async () => {
      const isRegistered = store.getters['medicalRecord/registeredInterview']
      await store.dispatch('advanceInterview/upsertMedicalRecordSection', {
        jwtToken: store.state.apiParams.jwtToken,
        treatmentConsultationSeq: store.state.apiParams.treatmentConsultationSeq,
        treatmentSerialNumber: store.state.apiParams.treatmentSerialNumber,
        userInfo: toUserInfoByState(store),
        recordTime: toRecordTimeByState(store),
        recordConsultation: toRecordConsultationByState(store),
        recordAssessment: toRecordAssessmentByState(store),
        recordPlan: toRecordPlanByState(store),
        statusCode: 2,
        isRegistered
      })
    }
    const clickedNextPage = async () => {
      isDisableSendBtn.value = true
      const result = await form.value.validate()
      if (!result.valid) {
        scrollToErrorPosition()
        return
      }
      try {
        await updateInterview()
        router.push('/record/done')
      } catch(e) {
        isDisableSendBtn.value = false
        errorDialog.value = true
        logger.error(e)
      }
    }
    return {
      fromConfirm,
      clickedNextPage,
      toBack,
      selectPlan,
      selectFlagIndicators,
      required,
      requiredOtherText,
      plan,
      planOther,
      prescription,
      form,
      isPrescriptions,
      prescriptionUrl,
      isDisableSendBtn,
      errorDialog,
      isError
    }
  }
})
</script>
